/* eslint-disable no-nested-ternary */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Grid2,
  Card,
  CardContent,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  Tooltip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FeedbackIcon from "@mui/icons-material/Feedback";
import SentimentVerySatisfiedOutlinedIcon from "@mui/icons-material/SentimentVerySatisfiedOutlined";
import { useSession } from "../../../../context/SessionContext"; // Adjust the path as needed
import axiosInstance from "../../../../api/axiosConfig";
import PlayerDailyReportModal from "./AddTrainingPage";

/**
 * Refactors:
 * 1) campId is no longer read from env. We fetch user's camps, let the user choose,
 *    and then fetch grouped sessions for the selected camp.
 * 2) Cleaner separation of concerns, stronger errors, and loading states.
 * 3) handleOpenModal now uses the currently selectedCampId and guards when not set.
 */
export default function PlayerDailySessionPage() {
  // --- Session / IDs ---
  const { session } = useSession();
  const userId = session?.user?.id;

  // --- State ---
  const [camps, setCamps] = useState([]); // list of user's camps
  const [campsLoading, setCampsLoading] = useState(true);
  const [selectedCampId, setSelectedCampId] = useState(null);

  const [groupedSessions, setGroupedSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  const [error, setError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);

  // --- Helpers (endpoints) ---
  const CAMPS_ENDPOINT = useCallback(
    (uid) => `/Training/api/Training/GetTrainingCampByUser?userId=${uid}`,
    [],
  );
  const SESSIONS_ENDPOINT = useCallback(
    (uid, campId) =>
      `/Training/api/Training/GetGroupedSession?UserId=${uid}&campId=${campId}`,
    [],
  );

  // --- Fetch user's camps ---
  useEffect(() => {
    const loadCamps = async () => {
      if (!userId) {
        setError("شناسه کاربر یافت نشد.");
        setCampsLoading(false);
        return;
      }

      try {
        setCampsLoading(true);
        setError("");
        const res = await axiosInstance.get(CAMPS_ENDPOINT(userId), {
          headers: { accept: "text/plain" },
        });

        // Some backends may return JSON with content-type text/plain; handle both.
        const raw =
          typeof res.data === "string" ? JSON.parse(res.data) : res.data;
        const normalized = Array.isArray(raw) ? raw : [];

        setCamps(normalized);

        // Auto-select camp with the highest trainigCampId
        if (normalized.length > 0) {
          const ids = normalized
            .map((c) => Number(c?.trainigCampId))
            .filter((n) => Number.isFinite(n));
          setSelectedCampId(ids.length ? Math.max(...ids) : null);
        } else {
          setSelectedCampId(null);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || "خطا در دریافت اردوهای کاربر.";
        setError(`خطا: ${msg}`);
      } finally {
        setCampsLoading(false);
      }
    };

    loadCamps();
  }, [userId, CAMPS_ENDPOINT]);

  // --- Fetch grouped sessions when camp changes ---
  useEffect(() => {
    const loadSessions = async () => {
      if (!userId) return; // handled above
      if (!selectedCampId) {
        setGroupedSessions([]);
        return;
      }

      try {
        setSessionsLoading(true);
        setError("");
        const res = await axiosInstance.get(
          SESSIONS_ENDPOINT(userId, selectedCampId),
        );
        setGroupedSessions(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        const msg =
          err?.response?.data?.message || "خطا در دریافت جلسات روزانه بازیکن.";
        setError(`خطا: ${msg}`);
      } finally {
        setSessionsLoading(false);
      }
    };

    loadSessions();
  }, [userId, selectedCampId, SESSIONS_ENDPOINT]);

  // --- Modal logic (uses the selected camp id) ---
  const handleOpenModal = useCallback(
    (date) => {
      if (!selectedCampId || !userId) return; // guard
      setModalData({ userId, campId: selectedCampId, date });
      setModalOpen(true);
    },
    [selectedCampId, userId],
  );

  const campMenuItems = useMemo(
    () =>
      camps.map((c) => (
        <MenuItem key={c.trainigCampId} value={c.trainigCampId}>
          {c.campTitle || `اردو ${c.trainigCampId}`}
        </MenuItem>
      )),
    [camps],
  );

  // --- Loading / Error ---
  if (campsLoading) {
    return (
      <CircularProgress sx={{ display: "block", margin: "0 auto", mt: 4 }} />
    );
  }

  if (error) {
    return (
      <Stack sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mx: "auto", width: "fit-content" }}>
          {error}
        </Alert>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        direction: "rtl",
        border: "1px solid #ccc",
        borderRadius: "8px",
        backgroundColor: "#fff",
        p: 2,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            لیست برنامه‌ها
          </Typography>
          <Typography variant="subtitle2" color="warning.main">
            توجه : ساعت برنامه‌ها دقیق نمی‌باشد و ساعت دقیق باید از مربی گرفته
            شود
          </Typography>
        </Box>

        {/* Camp selector */}
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="camp-select-label">انتخاب اردو</InputLabel>
          <Select
            labelId="camp-select-label"
            id="camp-select"
            label="انتخاب اردو"
            value={selectedCampId ?? ""}
            onChange={(e) => setSelectedCampId(e.target.value)}
          >
            {campMenuItems}
          </Select>
        </FormControl>
      </Stack>

      {/* Sessions */}
      {sessionsLoading ? (
        <CircularProgress sx={{ display: "block", margin: "24px auto" }} />
      ) : groupedSessions?.length === 0 ? (
        <Typography variant="body1" sx={{ mt: 3, textAlign: "center" }}>
          برای این اردو برنامه‌ای یافت نشد.
        </Typography>
      ) : (
        groupedSessions.map((group) => (
          <Box key={group.date} sx={{ mb: 5 }}>
            {/* Date Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 2,
                p: 2,
                border: "1px solid #ccc",
                borderRadius: "8px",
              }}
            >
              <Typography variant="h6">تاریخ: {group.date}</Typography>

              <Tooltip
                title={
                  selectedCampId
                    ? "ثبت عملکرد روزانه"
                    : "ابتدا اردو را انتخاب کنید"
                }
              >
                <span>
                  {/* span wrapper so Tooltip works when Button is disabled */}
                  <Button
                    variant="contained"
                    color="secondary"
                    size="medium"
                    startIcon={<FeedbackIcon sx={{ ml: 2 }} />}
                    disabled={!selectedCampId}
                    onClick={() => handleOpenModal(group.date)}
                  >
                    ثبت عملکرد روزانه
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/* Sessions Grid2 */}
            <Grid2 container spacing={3}>
              {group.sessions?.map((s) => (
                <Grid2 xs={12} sm={6} md={4} key={s.sessionId}>
                  <Card
                    sx={{
                      borderRadius: "10px",
                      boxShadow: "5px 5px 5px 2px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        {s.description}
                      </Typography>
                      {s.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, mb: 2 }}
                        >
                          {s.description}
                        </Typography>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        <AccessTimeIcon color="action" />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: "4px" }}
                        >
                          {s.startTime} - {s.endTime}
                        </Typography>
                        <SentimentVerySatisfiedOutlinedIcon
                          sx={{ color: "green" }}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        ))
      )}

      {/* Modal for Daily Report */}
      {modalData && (
        <PlayerDailyReportModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          data={modalData}
        />
      )}
    </Box>
  );
}
